import { axiosWithToken, axiosWithoutToken } from "@/utils/axios";
import parseHttpError from "@/utils/parseHttpError";

export type MarketingEventName =
  | "user_explored_properties"
  | "user_checked_weekly_deals"
  | "user_logged_in"
  | "user_explored_managed_props"
  | "user_viewed_property";

export type MarketingEventProps = Record<string, string | number | boolean>;

export interface MarketingIdentifyPayload {
  /**
   * Optional explicit consent capture. Omit when simply re-syncing the
   * current user — the backend will pull all profile fields from the
   * authenticated user record (the client payload is never trusted).
   */
  consent?: {
    newsletter: boolean;
    source: string;
  };
}

class MarketingService {
  /**
   * Send an authenticated client-side event to the backend. Backend will
   * fan it out to Mailchimp via the marketing queue. Failures are silent
   * by design — marketing must never break the UX.
   */
  async trackEvent(
    name: MarketingEventName,
    props: MarketingEventProps = {},
  ): Promise<void> {
    try {
      await axiosWithToken().post("/marketing/events", { name, props });
    } catch (err) {
      // Swallow — analytics must not break UI flows.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[marketing] trackEvent failed", name, err);
      }
    }
  }

  /**
   * Push canonical user profile fields to Mailchimp. Used right after
   * login or profile changes. Authenticated.
   */
  async identify(payload: MarketingIdentifyPayload = {}): Promise<void> {
    try {
      await axiosWithToken().post("/marketing/identify", payload);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[marketing] identify failed", err);
      }
    }
  }

  /**
   * Public newsletter subscription (no auth required). Used by the
   * footer signup form.
   */
  async subscribeNewsletter(payload: {
    email: string;
    first_name?: string;
    last_name?: string;
    source?: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await axiosWithoutToken.post(
        "/marketing/newsletter",
        payload,
      );
      return { success: true, ...(res.data ?? {}) };
    } catch (err: any) {
      return {
        success: false,
        message: parseHttpError(err).message,
      };
    }
  }
}

export const marketingService = new MarketingService();
export default marketingService;
